import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useLayoutEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { exposeTestId } from '../../config';
import { setConstraints } from '../../store/birdLayout/BirdLayoutState';
import { CheckboxListState } from '../../store/birdElement/checkboxListState';
import { selectBirdCheckboxListState, selectStyleState, selectAccessibilityLabelState, selectLayoutState, } from '../../store/selectors';
import { useDesignTokens } from '@sas/nova-commons/theme';
import { fontStyles, textHeight, longestTextWidth } from '../utils/style';
import { useFontLoadedKey } from '../FontProvider';
import { SearchableList } from '../SearchableList';
import { Link } from '@sas/nova-commons/components/Link';
import { useI18n } from '../../locale/I18nContext';
import { useListItemHeightCache } from '../SearchableList/useListItemHeightCache';
const checkboxListItemWidthPadding = 40 /*checkbox column*/ + 8 + 26 /*padding*/ + 2; /*border*/
export const BirdCheckboxListBase = function (props) {
    var _a, _b;
    const actions = props.actions;
    const { values, measureValues, selectedIndices, required, selectAllEnabled, searchable, searchValue, } = props.state;
    const { font } = props.style || {};
    const localize = useI18n();
    let itemHeight;
    const [measurements, setMeasurements] = useState();
    {
        const dt = useDesignTokens();
        const fonts = useMemo(() => fontStyles(font, dt), [font, dt]);
        const uiFonts = useMemo(() => fontStyles(undefined, dt), [dt]);
        const fontKey = useFontLoadedKey(fonts.fontFamily);
        const preferredValues = props.preferredSizing ? values : undefined;
        const preferredMeasureValues = props.preferredSizing ? measureValues : undefined;
        const { itemHeight: _itemHeight, refCache } = useListItemHeightCache(values, fonts, fontKey);
        itemHeight = _itemHeight;
        useLayoutEffect(() => {
            const heightCache = refCache.current; // guaranteed to be defined
            const selectAllHeight = selectAllEnabled ? textHeight(uiFonts) + 3 /* padding-bottom */ : 0;
            const searchHeight = searchable
                ? Math.max(28, textHeight(uiFonts) + 4 /* padding */) +
                    1 /* input fudge factor */ +
                    2 /* border */ +
                    6 /* padding around */
                : 0;
            const constraints = {
                minHeight: heightCache.getBaseItemHeight() * 2 + searchHeight + selectAllHeight,
                minWidth: 160,
            };
            if (preferredValues) {
                constraints.preferredWidth =
                    checkboxListItemWidthPadding +
                        longestTextWidth(preferredValues, fonts) +
                        (preferredMeasureValues
                            ? 8 /* text gap */ +
                                Math.max(24 /* min-width */, longestTextWidth(preferredMeasureValues, fonts))
                            : 0);
                constraints.preferredHeight = heightCache.getTotalHeight() + searchHeight + selectAllHeight;
            }
            setMeasurements({
                preferredWidth: constraints.preferredWidth,
                preferredHeight: constraints.preferredHeight,
            });
            actions.setConstraints(props.id, constraints);
        }, [
            fontKey /*(implicit)*/,
            preferredValues,
            preferredMeasureValues,
            actions,
            props.id,
            fonts,
            uiFonts,
            searchable,
            selectAllEnabled,
            refCache,
        ]);
    }
    const valueCount = (_a = values === null || values === void 0 ? void 0 : values.length) !== null && _a !== void 0 ? _a : 0;
    const selectedCount = (_b = selectedIndices === null || selectedIndices === void 0 ? void 0 : selectedIndices.length) !== null && _b !== void 0 ? _b : 0;
    const selectAllProps = useMemo(() => {
        if (!selectAllEnabled || (searchValue && (searchValue === null || searchValue === void 0 ? void 0 : searchValue.length) > 0)) {
            return;
        }
        if (required) {
            if (selectedCount === valueCount) {
                return {
                    text: localize({ key: 'CheckboxList.clearSelection.txt' }),
                    onPress: () => actions.update(props.id, { selectedIndices: [0] }),
                };
            }
            else {
                return {
                    text: localize({ key: 'CheckboxList.selectAll.txt' }),
                    onPress: () => actions.update(props.id, { selectedIndices: [...Array(valueCount).keys()] }),
                };
            }
        }
        if (selectedCount > 0) {
            return {
                text: localize({
                    key: 'CheckboxList.clearSelection.fmt',
                    args: { count: `${selectedCount}` },
                }),
                onPress: () => actions.update(props.id, { selectedIndices: [] }),
            };
        }
        else {
            return {
                text: localize({ key: 'CheckboxList.selectAll.txt' }),
                onPress: () => actions.update(props.id, { selectedIndices: [...Array(valueCount).keys()] }),
            };
        }
    }, [
        actions,
        props.id,
        selectAllEnabled,
        required,
        valueCount,
        selectedCount,
        searchValue,
        localize,
    ]);
    return (_jsx(SearchableList, { searchable: searchable, type: "multi", values: values, measureValues: measureValues, listTestId: exposeTestId() ? `${props.id}-CbList` : null, accessibilityLabel: props.accessibilityLabel, styleState: props.style, selection: selectedIndices !== null && selectedIndices !== void 0 ? selectedIndices : [], searchValue: searchValue !== null && searchValue !== void 0 ? searchValue : '', onSearchValueChange: (value) => actions.update(props.id, { searchValue: value }), onSelect: (newSelectedIndices) => {
            if (required && newSelectedIndices.length === 0 && selectedIndices) {
                // All selections were cleared in a required List.
                // Undo the change in ListState with a shallow copy of the old selections.
                newSelectedIndices = [...selectedIndices];
            }
            actions.update(props.id, { selectedIndices: newSelectedIndices });
        }, itemHeight: itemHeight, preferredWidth: measurements === null || measurements === void 0 ? void 0 : measurements.preferredWidth, preferredHeight: measurements === null || measurements === void 0 ? void 0 : measurements.preferredHeight, renderListWrapper: selectAllProps
            ? function renderSelectAll(list) {
                return (_jsxs("div", Object.assign({ style: {
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        minHeight: 0,
                    } }, { children: [_jsx("div", Object.assign({ style: { padding: '0 0 0.1875rem 0.5625rem' } }, { children: _jsx(Link, Object.assign({ onPress: selectAllProps.onPress, wrapText: false }, { children: selectAllProps.text })) })), list] })));
            }
            : undefined }));
};
export const BirdCheckboxList = connect((state, props) => ({
    state: selectBirdCheckboxListState(state, props.id),
    layoutState: selectLayoutState(state, props.id),
    style: selectStyleState(state, props.id),
    accessibilityLabel: selectAccessibilityLabelState(state, props.id),
}), (dispatch) => ({
    actions: bindActionCreators(Object.assign({ setConstraints }, CheckboxListState.actions), dispatch),
}))(BirdCheckboxListBase);
